import { useState, type ReactNode } from "react";
import { CalendarCheck, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import {
  useFinanceBudgetMutations,
  useFinanceBudgets,
  useFinanceEntryMutations,
  useFinanceGoalMutations,
  useFinanceGoals,
  useFinanceMonth,
  type EntryInput
} from "../api/hooks";
import type { FinanceBudget, FinanceCategory, FinanceEntry, FinanceGoal } from "../api/types";
import { MONTHS_LONG, addMonths, monthYearLabel, parts } from "../lib/date";
import { dueStatus, sumCents, type DueStatus } from "../lib/finance";
import { centsToInput, formatBRL, parseBRL } from "../lib/money";
import { useUi } from "../state/ui";
import { PageHead } from "./TasksPage";
import { Segmented, StatusChip, type Tone } from "./life";
import "./pages.css";
import "./life.css";

const STATUS: Record<DueStatus, { tone: Tone; label: (days: number | null, kind: FinanceEntry["kind"]) => string } | null> = {
  paid: { tone: "success", label: (_d, kind) => (kind === "income" ? "Recebida" : "Paga") },
  overdue: { tone: "danger", label: (_d, kind) => (kind === "income" ? "Não recebida" : "Atrasada") },
  today: { tone: "warning", label: (_d, kind) => (kind === "income" ? "Entra hoje" : "Vence hoje") },
  soon: { tone: "warning", label: (d, kind) => `${kind === "income" ? "Entra" : "Vence"} em ${d} ${d === 1 ? "dia" : "dias"}` },
  open: null
};

/** The entry's category (entries from before categories fall back to "Outros"). */
function useCategoryOf() {
  const { me } = useUi();
  return (key: string | undefined, kind: FinanceEntry["kind"]): FinanceCategory | undefined =>
    me.financeCategories.find((c) => c.key === key) ?? me.financeCategories.find((c) => c.kind === kind && /^outr/.test(c.key));
}

function fail(ui: ReturnType<typeof useUi>) {
  return (err: unknown) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`);
}

/** Inline form shared by "add" and "edit" of an entry. */
function EntryForm({
  kind,
  initial,
  onSubmit,
  onCancel,
  submitLabel
}: {
  kind: FinanceEntry["kind"];
  /** `category: ""` = let the server guess it from the description. */
  initial: { title: string; amount: string; dueDay: string; fixed: boolean; category: string };
  onSubmit: (v: { title: string; amountCents: number; dueDay: number | null; fixed: boolean; category?: string }) => void;
  onCancel?: () => void;
  submitLabel: string;
}) {
  const { me } = useUi();
  const [v, setV] = useState(initial);
  const cents = parseBRL(v.amount);
  const day = v.dueDay.trim() ? Number(v.dueDay) : null;
  const dayOk = day === null || (Number.isInteger(day) && day >= 1 && day <= 31);
  const valid = v.title.trim().length > 0 && cents !== null && dayOk;
  return (
    <form
      className="life-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({ title: v.title.trim(), amountCents: cents!, dueDay: day, fixed: v.fixed, category: v.category || undefined });
        if (!onCancel) setV({ ...initial, fixed: v.fixed });
      }}
    >
      <label className="life-field grow">
        <span>Descrição</span>
        <input className="field filled" autoFocus={Boolean(onCancel)} value={v.title} maxLength={120} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Aluguel, salário, mercado…" />
      </label>
      <label className="life-field">
        <span>Valor (R$)</span>
        <input
          className={`field filled tabular${v.amount && cents === null ? " invalid" : ""}`}
          inputMode="decimal"
          value={v.amount}
          onChange={(e) => setV({ ...v, amount: e.target.value })}
          placeholder="0,00"
        />
      </label>
      <label className="life-field">
        <span>Categoria</span>
        <select className="chip-select life-select" value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })}>
          {!initial.category && <option value="">Automática</option>}
          {me.financeCategories
            .filter((c) => c.kind === kind)
            .map((c) => (
              <option key={c.key} value={c.key}>
                {c.emoji} {c.label}
              </option>
            ))}
        </select>
      </label>
      <label className="life-field xs">
        <span>Dia</span>
        <input
          className={`field filled tabular${dayOk ? "" : " invalid"}`}
          inputMode="numeric"
          value={v.dueDay}
          maxLength={2}
          onChange={(e) => setV({ ...v, dueDay: e.target.value.replace(/\D/g, "") })}
          placeholder="—"
        />
      </label>
      <label className="checkbox life-check">
        <input type="checkbox" checked={v.fixed} onChange={(e) => setV({ ...v, fixed: e.target.checked })} />
        Todo mês
      </label>
      <div className="life-form-actions">
        {onCancel && (
          <button type="button" className="btn text" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn primary" disabled={!valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function EntryRow({ e, month }: { e: FinanceEntry; month: string }) {
  const ui = useUi();
  const { create, update, remove } = useFinanceEntryMutations(month);
  const categoryOf = useCategoryOf();
  const [editing, setEditing] = useState(false);
  const saving = e.id.startsWith("tmp-");
  const cat = categoryOf(e.category, e.kind);
  const { status, days } = dueStatus(e, ui.today);
  const chip = STATUS[status];
  const income = e.kind === "income";

  if (editing) {
    return (
      <li className="row row-editing">
        <EntryForm
          kind={e.kind}
          initial={{ title: e.title, amount: centsToInput(e.amountCents), dueDay: e.dueDay ? String(e.dueDay) : "", fixed: e.fixed, category: cat?.key ?? "" }}
          submitLabel="Salvar"
          onCancel={() => setEditing(false)}
          onSubmit={(v) => {
            setEditing(false);
            update.mutate({ item: e, ...v }, { onError: fail(ui) });
          }}
        />
      </li>
    );
  }

  const toggle = () =>
    update.mutate(
      { item: e, paid: !e.paid },
      {
        onSuccess: () =>
          !e.paid &&
          ui.showToast(income ? "Entrada recebida" : "Conta paga", { label: "Desfazer", run: () => update.mutate({ item: { ...e, paid: true }, paid: false }) }),
        onError: fail(ui)
      }
    );

  const removeIt = () =>
    remove.mutate(e, {
      onSuccess: () =>
        ui.showToast(income ? "Entrada excluída" : "Despesa excluída", {
          label: "Desfazer",
          run: () =>
            create.mutate(
              { month: e.month, kind: e.kind, title: e.title, amountCents: e.amountCents, dueDay: e.dueDay, fixed: e.fixed, category: e.category },
              { onSuccess: (n) => e.paid && update.mutate({ item: n, paid: true }) }
            )
        }),
      onError: fail(ui)
    });

  const paidOn = e.paid && e.occurredOn ? `${income ? "recebido" : "pago"} ${e.occurredOn.slice(8, 10)}/${e.occurredOn.slice(5, 7)}` : null;
  const sub = [cat?.label, e.dueDay ? (e.dueDay >= 31 ? "último dia" : `dia ${String(e.dueDay).padStart(2, "0")}`) : null, e.fixed ? "todo mês" : null, paidOn].filter(Boolean).join(" · ");

  return (
    <li className={`row money-row${e.paid ? " done" : ""}${status === "overdue" ? " overdue" : ""}`}>
      <label className="checkbox round row-check">
        <input
          type="checkbox"
          checked={e.paid}
          disabled={saving}
          onChange={toggle}
          aria-label={e.paid ? `Marcar ${e.title} como ${income ? "não recebida" : "não paga"}` : `Marcar ${e.title} como ${income ? "recebida" : "paga"}`}
        />
      </label>
      <button className="row-main" disabled={saving} onClick={() => setEditing(true)} title="Clique para editar">
        <span className="cat-emoji" aria-hidden>
          {cat?.emoji}
        </span>
        <span className="row-stack">
          <span className="row-title">{e.title}</span>
          <span className="row-sub">
            {sub}
            {e.onAgenda && !e.paid && (
              <span className="fin-agenda" title="Na agenda, com lembrete no WhatsApp na véspera e no dia">
                <CalendarCheck size={12} aria-hidden /> na agenda
              </span>
            )}
          </span>
        </span>
      </button>
      <span className="row-meta">
        {chip && <StatusChip tone={chip.tone}>{chip.label(days, e.kind)}</StatusChip>}
        <span className={`money${income ? " income" : ""}`}>{formatBRL(e.amountCents)}</span>
        <button className="icon-btn sm row-delete" aria-label={`Excluir ${e.title}`} disabled={saving} onClick={removeIt}>
          <Trash2 size={15} />
        </button>
      </span>
    </li>
  );
}

/** Row section with the sum of its entries on the right. */
function MoneySection({ title, entries, children }: { title: string; entries: FinanceEntry[]; children: ReactNode }) {
  return (
    <section className="row-section">
      <h2 className="row-section-title eyebrow money-head">
        {title} <span>{String(entries.length).padStart(2, "0")}</span>
        <span className="money-head-total">{formatBRL(sumCents(entries))}</span>
      </h2>
      <ul className="rows">{children}</ul>
    </section>
  );
}

/** "↗ 12% a mais que setembro" (closed months) or "já 80% de setembro" (the month still running). */
function useComparison(month: string, expenses: FinanceEntry[]): ReactNode {
  const ui = useUi();
  const prev = addMonths(`${month}-01`, -1).slice(0, 7);
  const { data } = useFinanceMonth(prev);
  const before = data?.month === prev ? sumCents(data.entries.filter((e) => e.kind === "expense")) : 0;
  const now = sumCents(expenses);
  if (!before || !now) return null;
  const pct = Math.round((now / before) * 100);
  if (month === ui.today.slice(0, 7)) return `despesas já em ${pct}% das de ${monthName(prev)}`;
  if (pct === 100) return `despesas iguais às de ${monthName(prev)}`;
  return <span className={pct > 100 ? "danger" : undefined}>{`despesas ${Math.abs(pct - 100)}% ${pct > 100 ? "acima" : "abaixo"} de ${monthName(prev)}`}</span>;
}

function Summary({ entries, month }: { entries: FinanceEntry[]; month: string }) {
  const ui = useUi();
  const income = entries.filter((e) => e.kind === "income");
  const expenses = entries.filter((e) => e.kind === "expense");
  const balance = sumCents(income) - sumCents(expenses);
  const toPay = expenses.filter((e) => !e.paid);
  const late = toPay.filter((e) => dueStatus(e, ui.today).status === "overdue");
  const comparison = useComparison(month, expenses);
  const notes: ReactNode[] = [
    toPay.length ? `${formatBRL(sumCents(toPay))} ainda a pagar` : expenses.length ? "Tudo pago" : null,
    late.length ? <span className="danger">{`${late.length} ${late.length === 1 ? "conta atrasada" : "contas atrasadas"}`}</span> : null,
    income.some((e) => !e.paid) ? `${formatBRL(sumCents(income.filter((e) => !e.paid)))} a receber` : null,
    comparison
  ].filter(Boolean);
  return (
    <div className="card fin-stat-card">
      <div className="fin-stat-grid">
        <div className="fin-stat">
          <span className="fin-stat-label">Entradas</span>
          <span className="fin-stat-value">{formatBRL(sumCents(income))}</span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat-label">Despesas</span>
          <span className="fin-stat-value">{formatBRL(sumCents(expenses))}</span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat-label">Saldo do mês</span>
          <span className={`fin-stat-value ${balance < 0 ? "negative" : balance > 0 ? "positive" : ""}`}>{formatBRL(balance)}</span>
        </div>
      </div>
      {notes.length > 0 && (
        <p className="fin-stat-note">
          {notes.map((n, i) => (
            <span key={i}>
              {i > 0 && " · "}
              {n}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/** Expenses by category: one bar per category, biggest first, as a share of the month's expenses. */
function ByCategory({ entries }: { entries: FinanceEntry[] }) {
  const categoryOf = useCategoryOf();
  const expenses = entries.filter((e) => e.kind === "expense");
  const total = sumCents(expenses);
  if (!total) return null;
  const groups = new Map<string, { cat: FinanceCategory | undefined; cents: number; count: number }>();
  for (const e of expenses) {
    const cat = categoryOf(e.category, e.kind);
    const k = cat?.key ?? "outros";
    const g = groups.get(k) ?? { cat, cents: 0, count: 0 };
    groups.set(k, { cat, cents: g.cents + e.amountCents, count: g.count + 1 });
  }
  const rows = [...groups.values()].sort((a, b) => b.cents - a.cents);
  return (
    <section className="row-section">
      <h2 className="row-section-title eyebrow">
        Despesas por categoria <span>{String(rows.length).padStart(2, "0")}</span>
      </h2>
      <ul className="cat-list">
        {rows.map(({ cat, cents, count }) => {
          const pct = Math.round((cents / total) * 100);
          return (
            <li key={cat?.key ?? "outros"} className="cat-row">
              <span className="cat-name">
                <span aria-hidden>{cat?.emoji}</span> {cat?.label ?? "Outros"}
                <span className="cat-count">{count}</span>
              </span>
              <span className="cat-bar" aria-hidden>
                <span style={{ width: `${Math.max(pct, 2)}%` }} />
              </span>
              <span className="cat-pct">{pct}%</span>
              <span className="money">{formatBRL(cents)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function monthName(month: string): string {
  return MONTHS_LONG[parts(`${month}-01`).m - 1];
}

function Month({ month }: { month: string }) {
  const ui = useUi();
  const { data } = useFinanceMonth(month);
  const { create, copyFixed } = useFinanceEntryMutations(month);
  const [kind, setKind] = useState<EntryInput["kind"]>("expense");
  const entries = data?.month === month ? data.entries : [];
  const income = entries.filter((e) => e.kind === "income");
  const expenses = entries.filter((e) => e.kind === "expense");
  const copyable = data?.month === month ? data.copyable : null;

  return (
    <>
      <Summary entries={entries} month={month} />

      {copyable && copyable.count > 0 && copyable.from && (
        <div className="life-banner">
          <span>
            {copyable.count} {copyable.count === 1 ? "lançamento fixo" : "lançamentos fixos"} de {monthName(copyable.from)} ainda não {copyable.count === 1 ? "está" : "estão"} neste mês.
          </span>
          <button
            className="btn outline"
            disabled={copyFixed.isPending}
            onClick={() =>
              copyFixed.mutate(undefined, {
                onSuccess: ({ added }) => ui.showToast(`${added} ${added === 1 ? "lançamento copiado" : "lançamentos copiados"}`),
                onError: fail(ui)
              })
            }
          >
            Trazer para {monthName(month)}
          </button>
        </div>
      )}

      <section className="row-section">
        <div className="add-head">
          <h2 className="row-section-title eyebrow add-head-title">Novo lançamento</h2>
          <Segmented
            value={kind}
            onChange={setKind}
            label="Tipo de lançamento"
            options={[
              { value: "expense", label: "Despesa" },
              { value: "income", label: "Entrada" }
            ]}
          />
        </div>
        <EntryForm
          key={kind}
          kind={kind}
          initial={{ title: "", amount: "", dueDay: "", fixed: false, category: "" }}
          submitLabel={kind === "expense" ? "Adicionar despesa" : "Adicionar entrada"}
          onSubmit={(v) => create.mutate({ month, kind, ...v }, { onError: fail(ui) })}
        />
      </section>

      {data?.month !== month ? (
        <div className="center-fill" style={{ height: 160 }}>
          <div className="spinner" />
        </div>
      ) : !entries.length ? (
        <p className="empty">Nada lançado em {monthName(month)}.</p>
      ) : (
        <>
          {expenses.length > 0 && (
            <MoneySection title="Despesas" entries={expenses}>
              {expenses.map((e) => <EntryRow key={e.id} e={e} month={month} />)}
            </MoneySection>
          )}
          {income.length > 0 && (
            <MoneySection title="Entradas" entries={income}>
              {income.map((e) => <EntryRow key={e.id} e={e} month={month} />)}
            </MoneySection>
          )}
          <ByCategory entries={entries} />
        </>
      )}
      {data?.month === month && <Budgets entries={entries} />}
    </>
  );
}

// --- Limites por categoria -----------------------------------------------------------------------

function BudgetRow({ b, spent, cat }: { b: FinanceBudget; spent: number; cat: FinanceCategory | undefined }) {
  const ui = useUi();
  const { set, remove } = useFinanceBudgetMutations();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(centsToInput(b.limitCents));
  const cents = parseBRL(value);
  const pct = Math.round((spent / b.limitCents) * 100);
  const left = b.limitCents - spent;
  const tone: Tone = pct > 100 ? "danger" : pct >= 80 ? "warning" : "success";
  return (
    <li className="goal">
      <div className="goal-top">
        <span className="goal-title">
          <span aria-hidden>{cat?.emoji}</span> {cat?.label ?? b.category}
        </span>
        <StatusChip tone={tone}>{left >= 0 ? `Restam ${formatBRL(left)}` : `Passou ${formatBRL(-left)}`}</StatusChip>
      </div>
      <div className={`meter budget-${tone}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(pct, 100)} aria-label={`${cat?.label}: ${pct}% do limite`}>
        <span style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {editing ? (
        <form
          className="life-form goal-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!cents) return;
            setEditing(false);
            set.mutate({ category: b.category, limitCents: cents }, { onError: fail(ui) });
          }}
        >
          <label className="life-field">
            <span>Limite por mês (R$)</span>
            <input className="field filled tabular" autoFocus inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </label>
          <div className="life-form-actions">
            <button type="button" className="btn text" onClick={() => setEditing(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn primary" disabled={!cents}>
              Salvar
            </button>
          </div>
        </form>
      ) : (
        <div className="goal-bottom">
          <span className="goal-amounts">
            {formatBRL(spent)} <span>de {formatBRL(b.limitCents)} · {pct}%</span>
          </span>
          <span className="goal-actions">
            <button className="btn text" onClick={() => setEditing(true)}>
              Alterar
            </button>
            <button
              className="icon-btn sm"
              aria-label={`Tirar o limite de ${cat?.label ?? b.category}`}
              onClick={() =>
                remove.mutate(b, {
                  onSuccess: () => ui.showToast("Limite removido", { label: "Desfazer", run: () => set.mutate(b) }),
                  onError: fail(ui)
                })
              }
            >
              <Trash2 size={15} />
            </button>
          </span>
        </div>
      )}
    </li>
  );
}

/** Monthly limits per category, against what the month shown spent in each. */
function Budgets({ entries }: { entries: FinanceEntry[] }) {
  const ui = useUi();
  const categoryOf = useCategoryOf();
  const { data } = useFinanceBudgets();
  const { set } = useFinanceBudgetMutations();
  const budgets = data ?? [];
  const free = ui.me.financeCategories.filter((c) => c.kind === "expense" && !budgets.some((b) => b.category === c.key));
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const cents = parseBRL(amount);
  const chosen = category || free[0]?.key || "";
  const spentIn = (key: string) => sumCents(entries.filter((e) => e.kind === "expense" && categoryOf(e.category, e.kind)?.key === key));

  return (
    <section className="row-section">
      <h2 className="row-section-title eyebrow">
        Limites por categoria <span>{String(budgets.length).padStart(2, "0")}</span>
      </h2>
      {budgets.length > 0 ? (
        <ul className="goal-list">
          {budgets.map((b) => (
            <BudgetRow key={b.category} b={b} spent={spentIn(b.category)} cat={ui.me.financeCategories.find((c) => c.key === b.category)} />
          ))}
        </ul>
      ) : (
        <p className="life-hint">Defina quanto quer gastar por mês em uma categoria — o assistente avisa no WhatsApp quando chegar perto. Também por lá: “limite de 1500 pra mercado”.</p>
      )}
      {free.length > 0 && (
        <form
          className="life-form card goal-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (!cents || !chosen) return;
            set.mutate({ category: chosen, limitCents: cents }, { onError: fail(ui) });
            setAmount("");
            setCategory("");
          }}
        >
          <label className="life-field grow">
            <span>Categoria</span>
            <select className="chip-select life-select" value={chosen} onChange={(e) => setCategory(e.target.value)}>
              {free.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.emoji} {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="life-field">
            <span>Limite por mês (R$)</span>
            <input className="field filled tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" />
          </label>
          <div className="life-form-actions">
            <button type="submit" className="btn primary" disabled={!cents}>
              Definir limite
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// --- Metas ---------------------------------------------------------------------------------------

function GoalRow({ g }: { g: FinanceGoal }) {
  const ui = useUi();
  const { create, update, remove } = useFinanceGoalMutations();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(centsToInput(g.savedCents));
  const [target, setTarget] = useState(centsToInput(g.targetCents));
  const pct = Math.min(100, Math.round((g.savedCents / g.targetCents) * 100));
  const saving = g.id.startsWith("tmp-");
  const savedC = parseBRL(saved);
  const targetC = parseBRL(target);

  return (
    <li className="goal">
      <div className="goal-top">
        <span className="goal-title">{g.title}</span>
        <span className="goal-pct">{pct}%</span>
      </div>
      <div className="meter" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${g.title}: ${pct}%`}>
        <span style={{ width: `${pct}%` }} className={pct >= 100 ? "full" : ""} />
      </div>
      {editing ? (
        <form
          className="life-form goal-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (savedC === null || !targetC) return;
            setEditing(false);
            update.mutate({ item: g, savedCents: savedC, targetCents: targetC }, { onError: fail(ui) });
          }}
        >
          <label className="life-field">
            <span>Guardado (R$)</span>
            <input className="field filled tabular" autoFocus inputMode="decimal" value={saved} onChange={(e) => setSaved(e.target.value)} />
          </label>
          <label className="life-field">
            <span>Meta (R$)</span>
            <input className="field filled tabular" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} />
          </label>
          <div className="life-form-actions">
            <button type="button" className="btn text" onClick={() => setEditing(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn primary" disabled={savedC === null || !targetC}>
              Salvar
            </button>
          </div>
        </form>
      ) : (
        <div className="goal-bottom">
          <span className="goal-amounts">
            {formatBRL(g.savedCents)} <span>de {formatBRL(g.targetCents)}</span>
          </span>
          <span className="goal-actions">
            <button className="btn text" disabled={saving} onClick={() => setEditing(true)}>
              Atualizar
            </button>
            <button
              className="icon-btn sm"
              aria-label={`Excluir ${g.title}`}
              disabled={saving}
              onClick={() =>
                remove.mutate(g, {
                  onSuccess: () =>
                    ui.showToast("Meta excluída", { label: "Desfazer", run: () => create.mutate({ title: g.title, targetCents: g.targetCents, savedCents: g.savedCents }) }),
                  onError: fail(ui)
                })
              }
            >
              <Trash2 size={15} />
            </button>
          </span>
        </div>
      )}
    </li>
  );
}

function Goals() {
  const ui = useUi();
  const { data } = useFinanceGoals();
  const { create } = useFinanceGoalMutations();
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [saved, setSaved] = useState("");
  const targetC = parseBRL(target);
  const savedC = saved.trim() ? parseBRL(saved) : 0;
  const valid = title.trim().length > 0 && Boolean(targetC) && savedC !== null;
  const goals = data ?? [];

  return (
    <section className="tile tile-parchment">
      <div className="page">
        <header className="col-head">
          <h2 className="col-title display">Metas</h2>
          <span className="eyebrow">Valem para todos os meses</span>
        </header>
        {goals.length > 0 ? <ul className="goal-list">{goals.map((g) => <GoalRow key={g.id} g={g} />)}</ul> : <p className="empty">Nenhuma meta ainda.</p>}
        <form
          className="life-form card goal-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            create.mutate({ title: title.trim(), targetCents: targetC!, savedCents: savedC! }, { onError: fail(ui) });
            setTitle("");
            setTarget("");
            setSaved("");
          }}
        >
          <label className="life-field grow">
            <span>Nova meta</span>
            <input className="field filled" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="Reserva de emergência, viagem…" />
          </label>
          <label className="life-field">
            <span>Meta (R$)</span>
            <input className="field filled tabular" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0,00" />
          </label>
          <label className="life-field">
            <span>Já guardado (R$)</span>
            <input className="field filled tabular" inputMode="decimal" value={saved} onChange={(e) => setSaved(e.target.value)} placeholder="0,00" />
          </label>
          <div className="life-form-actions">
            <button type="submit" className="btn primary" disabled={!valid}>
              Adicionar meta
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

/** "Finanças": each month's income and expenses (with due dates), plus savings goals. */
export function FinancePage() {
  const ui = useUi();
  const current = ui.today.slice(0, 7);
  const [month, setMonth] = useState(current);
  const step = (n: number) => setMonth(addMonths(`${month}-01`, n).slice(0, 7));

  return (
    <div className="page-scroll">
      <section className="tile">
        <div className="page">
          <PageHead label="Finanças" title="Seu dinheiro, mês a mês.">
            Gastos por categoria, contas com vencimento (na agenda, com lembrete) e limites. Também pelo WhatsApp: “gastei 50 no mercado”, “o que falta pagar?”, “meu
            saldo”.
          </PageHead>
          <div className="month-bar">
            <button className="icon-btn" aria-label="Mês anterior" onClick={() => step(-1)}>
              <ChevronLeft size={20} />
            </button>
            <h2 className="month-title display">{capitalize(monthYearLabel(`${month}-01`))}</h2>
            <button className="icon-btn" aria-label="Próximo mês" onClick={() => step(1)}>
              <ChevronRight size={20} />
            </button>
            {month !== current && (
              <button className="btn text" onClick={() => setMonth(current)}>
                Este mês
              </button>
            )}
          </div>
          <Month month={month} />
        </div>
      </section>
      <Goals />
    </div>
  );
}
